import englishMessages from 'ra-language-english';
import type { TranslationMessages } from 'ra-core';
import type { PayMessages } from './payMessages';

/**
 * English catalog: the official react-admin English messages (the full
 * `ra.*` vocabulary the framework's own components need) plus this
 * application's own `pay.*` domain strings.
 */
const en: TranslationMessages & { pay: PayMessages } = {
  ...englishMessages,
  pay: {
    appBar: {
      wordmarkBrand: 'Roommatik',
      wordmarkProduct: 'Pay',
      // v3.7 — the non-production rail/app-bar pill deliberately stays in
      // English in every locale (hybrid-3c.md, matches the reference apps).
      environmentTest: 'TEST ENV',
      environmentLocal: 'LOCAL',
    },
    nav: {
      operations: 'Operations',
      statistics: 'Statistics',
      tenants: 'Tenants',
      applications: 'Applications',
      credentials: 'Credentials',
      gatewayAccounts: 'Gateway accounts',
      audit: 'Audit',
      testBench: 'Test payment',
      groups: {
        operation: 'Operation',
        configuration: 'Configuration',
      },
    },
    profile: {
      roleLabelSuperAdmin: 'Platform general administrator',
      roleLabelAdmin: 'Platform administrator',
      roleLabelAuditor: 'Platform auditor',
      logout: 'Sign out',
    },
    sectionHeader: {
      help: 'Help',
      filters: 'Filters',
      refresh: 'Refresh',
      export: 'Export',
      create: 'Create',
    },
    filtersPopover: {
      title: 'Filters',
      apply: 'Apply',
      clear: 'Clear',
    },
    filterableSelect: {
      noOptions: 'No results',
      loading: 'Loading…',
      filterPlaceholder: 'Filter…',
    },
    emptyState: {
      comingSoonTitle: 'This section will be available soon',
      comingSoonBody: 'We are preparing this feature. Please check back later.',
    },
    loadingState: {
      label: 'Loading data…',
    },
    errorState: {
      title: 'The request could not be completed',
      body: 'Check your connection and try again. If the problem persists, contact support.',
      retry: 'Retry',
    },
    dataGrid: {
      showMore: 'Show more details',
      showLess: 'Show fewer details',
      displayedRows: '%{from}–%{to} of %{count}',
    },
    accessDenied: {
      title: 'You do not have permission to perform this action',
      body: 'Your role only allows viewing this section.',
    },
    noSession: {
      title: 'There is no active session',
      body: 'Sign in from the corresponding portal to continue. If the problem persists, contact support.',
    },
    notEntitled: {
      title: 'You do not have access to this application',
      body: 'Your account signed in successfully, but it does not have access to this application. Contact support to request it.',
    },
    accessUnavailable: {
      title: 'The access check could not be completed',
      body: 'Try again in a few moments. If the problem persists, contact technical support.',
    },
    operations: {
      pageTitle: 'Operations',
      pageSubtitle: 'Payment operations and aggregates by tenant.',
      tabs: { list: 'Operations', aggregates: 'Aggregates' },
      list: {
        emptyTitle: 'No operations found',
        emptyBody: 'Adjust the applied filters or widen the date range and try again.',
        errorTitle: 'The operations list could not be loaded',
        columnTenant: 'Tenant',
        columnApplication: 'Application',
        columnGateway: 'Gateway',
        columnType: 'Type',
        columnStatus: 'Status',
        columnAmount: 'Amount',
        columnOrderReference: 'Reference',
        columnCreatedAt: 'Created at',
        columnUpdatedAt: 'Last updated',
        viewDetail: 'View operation detail',
        pageLabel: 'Page',
        pageOf: 'of',
        rowsPerPage: 'Operations per page',
        exportStarted: 'The CSV file download has started.',
        exportError: 'The export file could not be generated.',
      },
      detail: {
        back: 'Back to the operations list',
        notFoundTitle: 'Operation not found',
        notFoundBody: 'Check the identifier or go back to the operations list.',
        accessDeniedTitle: 'You are not authorized to view this operation',
        fieldTenant: 'Tenant',
        fieldApplication: 'Owning application',
        fieldType: 'Type',
        fieldStatus: 'Status',
        fieldAmount: 'Amount',
        fieldOrderReference: 'Reference',
        fieldDescription: 'Description',
        fieldCustomerRef: 'Customer reference',
        fieldGateway: 'Gateway',
        fieldCreatedAt: 'Created at',
        fieldExpiresAt: 'Expires at',
        timelineTitle: 'Operation timeline',
        timelineEmpty: 'No event has been recorded for this operation yet.',
        operationAmount: 'Operation amount',
        operationReference: 'Gateway reference',
        gatewayCheckTitle: 'Gateway status',
        gatewayCheckNeverChecked: 'The gateway has not been asked for this payment’s status yet.',
        gatewayCheckUnverifiable: 'Could not be verified',
        gatewayCheckCheckedAt: 'Last checked',
        gatewayReconcileAction: 'Reconcile with the gateway',
        gatewayReconcileSuccess: 'The payment status was updated successfully.',
        gatewayReconcileUnsupported: 'This payment’s gateway does not support automatic status checks.',
      },
      aggregates: {
        emptyTitle: 'No aggregate data for the applied filters',
        emptyBody: 'Adjust the filters or the date range and try again.',
        fieldTotalCount: 'Operations created',
        fieldSuccessCount: 'Successful operations',
        fieldSuccessRate: 'Success rate',
        fieldAbandonmentRate: 'Abandonment rate',
        fieldTotalAmount: 'Total amount',
        fieldRefundedAmount: 'Refunded amount',
        groupByLabel: 'Group by',
        periodLabel: 'Period',
        bucketUnlabelled: 'No grouping',
      },
      filters: {
        dateFrom: 'Date from',
        dateTo: 'Date to',
        tenantCode: 'Tenants',
        tenantCodeHelp: 'Enter a tenant code and press Enter to add it.',
        tenantTag: 'Tenant tags',
        tenantTagHelp: 'key:value format, e.g. chain:acme. Press Enter to add it.',
        applicationCode: 'Applications',
        gateway: 'Gateway',
        status: 'Status',
        type: 'Type',
        amountMin: 'Minimum amount',
        amountMax: 'Maximum amount',
        amountHelp: 'In minor currency units (e.g. cents).',
        orderReference: 'Reference',
        sort: 'Sort by',
      },
      sort: {
        createdAtDesc: 'Created at (newest first)',
        createdAtAsc: 'Created at (oldest first)',
        amountDesc: 'Amount (highest first)',
        amountAsc: 'Amount (lowest first)',
      },
      status: {
        created: 'Created',
        session_consumed: 'Session consumed',
        redirected: 'Redirected',
        authorized: 'Authorized',
        captured: 'Captured',
        failed: 'Failed',
        cancelled: 'Cancelled',
        expired: 'Expired',
        refunded: 'Refunded',
        partially_refunded: 'Partially refunded',
      },
      type: {
        sale: 'Sale',
        preauth: 'Preauth',
        verify: 'Verify',
        terminal: 'Terminal',
      },
      operationKind: {
        authorize: 'Authorize',
        capture: 'Capture',
        void: 'Void',
        refund: 'Refund',
        increment: 'Increment',
      },
      operationStatus: {
        pending: 'Pending',
        succeeded: 'Succeeded',
        failed: 'Failed',
      },
      gatewayStatus: {
        awaiting_guest: 'The guest has not completed the payment process',
        attempt_declined: 'The payment attempt was declined',
        action_required: 'Pending an additional verification step',
        paid: 'The payment was completed at the gateway',
      },
      timelineEvent: {
        created: 'Operation created',
        session_consumed: 'Payment session consumed',
        redirected: 'Guest redirected',
        webhook_received: 'Notification received from the gateway',
        status_changed: 'Status changed',
        notification_sent: 'Notification sent to the application',
        notification_retry: 'Notification retry',
        notification_delivered: 'Notification delivered successfully',
      },
      period: {
        day: 'Day',
        week: 'Week',
        month: 'Month',
      },
      groupBy: {
        application: 'Application',
        tenant: 'Tenant',
        gateway: 'Gateway',
        period: 'Period',
        tag: 'Tag',
      },
    },
    statistics: {
      pageTitle: 'Statistics',
      pageSubtitle: 'Annual card-charge volume and breakdown by tenant and tag.',
      annual: {
        title: 'Card charges in %{year}',
        ofLimit: 'of %{limit} yearly charges',
        percentUsed: '%{percent} of the annual limit used',
        remaining: '%{remaining} charges remaining',
        warning: 'Over 80% of the annual charges limit has been used.',
        overLimit: 'The annual charges limit has been exceeded.',
        errorTitle: 'The annual counter could not be loaded',
      },
      tabs: {
        tenant: 'Tenant',
        tag: 'Tag',
        all: 'All',
      },
      views: {
        simple: 'Simple view',
        extended: 'Extended view',
      },
      breakdown: {
        untagged: 'Untagged',
        totalsTitle: 'Totals',
        emptyTitle: 'No operations in the selected period',
        emptyBody: 'Adjust the date range and try again.',
        errorTitle: 'The breakdown could not be loaded',
      },
      table: {
        columnBucket: 'Group',
        columnCurrency: 'Currency',
        columnCharges: 'Charges',
        columnStarted: 'Operations started',
        totalsLabel: 'Totals',
        totalsOverallLabel: 'Overall total',
      },
      exportStarted: 'The CSV file download has started.',
      exportError: 'The export file could not be generated.',
    },
    tenants: {
      pageTitle: 'Tenants',
      pageSubtitle: 'Tenants configured on the payments platform.',
      list: {
        emptyTitle: 'No tenants found',
        emptyBody: 'Adjust the applied filters or create a new tenant.',
        errorTitle: 'The tenants list could not be loaded',
        columnCode: 'Code',
        columnName: 'Name',
        columnStatus: 'Status',
        columnCreatedAt: 'Created at',
        viewDetail: 'View tenant detail',
        rowsPerPage: 'Tenants per page',
        createLabel: 'Create tenant',
      },
      filters: {
        search: 'Search',
        searchHelp: 'Search by tenant code or name.',
        status: 'Status',
      },
      status: {
        active: 'Active',
        suspended: 'Suspended',
      },
      create: {
        title: 'Create tenant',
        fieldCode: 'Code',
        fieldCodeHelp: 'Permanent identifier for the tenant. It cannot be changed afterwards.',
        fieldName: 'Name',
        submit: 'Create',
        cancel: 'Cancel',
        success: 'The tenant was created successfully.',
        conflictError: 'A tenant with this code already exists. Enter a different code.',
      },
      detail: {
        back: 'Back to the tenants list',
        notFoundTitle: 'Tenant not found',
        notFoundBody: 'Check the code or go back to the tenants list.',
        tabsGeneral: 'General',
        tabsPolicy: 'Policy',
        fieldCode: 'Code',
        fieldName: 'Name',
        fieldStatus: 'Status',
        fieldCreatedAt: 'Created at',
        editNameAction: 'Edit name',
        changeStatusAction: 'Change status',
      },
      editName: {
        title: 'Edit tenant name',
        fieldName: 'Name',
        submit: 'Save',
        cancel: 'Cancel',
        success: 'The name was updated successfully.',
      },
      statusDialog: {
        title: 'Change the tenant status',
        body: 'A suspended tenant keeps its existing data; only new payment-intent creation is blocked.',
        fieldStatus: 'New status',
        submit: 'Confirm',
        cancel: 'Cancel',
        success: 'The status was updated successfully.',
      },
      tags: {
        title: 'Replace tags',
        replaceNotice:
          "This operation replaces the tenant's entire tag set. Enter every tag you want to keep, not only the new ones.",
        empty: 'This tenant has no tags.',
        editAction: 'Replace tags',
        fieldKey: 'Key',
        fieldValue: 'Value',
        keyHelp: 'Lowercase letters, numbers, hyphens and underscores only.',
        add: 'Add tag',
        remove: 'Remove',
        submit: 'Save',
        cancel: 'Cancel',
        success: 'The tags were updated successfully.',
      },
      policy: {
        emptyTitle: 'This tenant has no policy configured',
        emptyBody:
          'Set up a policy to establish amount limits, currencies and allowed operation types.',
        createAction: 'Create policy',
        editAction: 'Edit policy',
        errorTitle: 'The tenant policy could not be loaded',
        fieldMaxAmount: 'Maximum amount',
        fieldMaxAmountHelp: 'In minor currency units (e.g. cents).',
        fieldCurrencies: 'Allowed currencies',
        fieldCurrenciesHelp: 'ISO 4217 code, e.g. EUR. Press Enter to add each currency.',
        fieldAllowedTypes: 'Allowed operation types',
        fieldAllowedReturnOrigins: 'Allowed return origins',
        fieldAllowedReturnOriginsHelp:
          'Allowed origin URLs for returnUrl and cancelUrl. Press Enter to add each one.',
        fieldConflictPolicy: 'Duplicate order reference policy',
        conflictPolicy: {
          reject: 'Reject the new operation',
          return_existing: 'Return the existing operation',
        },
        submit: 'Save',
        cancel: 'Cancel',
        success: 'The policy was saved successfully.',
      },
    },
    applications: {
      pageTitle: 'Applications',
      pageSubtitle: 'Applications registered to authenticate against the payments API.',
      list: {
        emptyTitle: 'No applications found',
        emptyBody: 'Create a calling application to allow it to inject payments.',
        errorTitle: 'The applications list could not be loaded',
        columnCode: 'Code',
        columnName: 'Name',
        columnStatus: 'Status',
        columnNotifyUrl: 'Notification URL',
        columnCreatedAt: 'Created at',
        viewDetail: 'View application detail',
        rowsPerPage: 'Applications per page',
        createLabel: 'Create application',
        notifyUrlEmpty: 'Not configured',
      },
      status: { active: 'Active', suspended: 'Suspended' },
      create: {
        title: 'Create application',
        fieldCode: 'Code',
        fieldCodeHelp:
          'Permanent public identifier for the application. It cannot be changed afterwards.',
        fieldName: 'Name',
        fieldNotifyUrl: 'Notification URL',
        fieldNotifySecret: 'Notification signing secret',
        fieldNotifySecretHelp:
          'Write-only. Required to sign outbound notifications if a notification URL is set.',
        submit: 'Create',
        cancel: 'Cancel',
        success: 'The application was created successfully.',
        conflictError: 'An application with this code already exists. Enter a different code.',
      },
      detail: {
        back: 'Back to the applications list',
        notFoundTitle: 'Application not found',
        notFoundBody: 'Check the code or go back to the applications list.',
        fieldCode: 'Code',
        fieldName: 'Name',
        fieldStatus: 'Status',
        fieldNotifyUrl: 'Notification URL',
        notifyUrlEmpty: 'Not configured',
        fieldCreatedAt: 'Created at',
        editAction: 'Edit application',
      },
      edit: {
        title: 'Edit application',
        fieldName: 'Name',
        fieldStatus: 'Status',
        fieldNotifyUrl: 'Notification URL',
        fieldNotifySecret: 'Notification signing secret',
        fieldNotifySecretHelp: 'Write-only. Leave blank to keep the existing secret unchanged.',
        submit: 'Save',
        cancel: 'Cancel',
        success: 'The application was updated successfully.',
      },
      egressIps: {
        title: 'Egress IP addresses',
        intro:
          'Charge creation is restricted by an edge access control: only authorized addresses can call.',
        errorTitle: 'The egress IP addresses could not be loaded',
        declaredTitle: 'Declared egress IP addresses',
        declaredEmpty: 'No addresses declared.',
        editAction: 'Edit declared addresses',
        chipListed: 'On the authorized list',
        chipMissing: 'Missing from the list',
        chipUnverified: 'Not verified',
        verificationUnavailable: 'The edge access control could not be checked.',
        notApplicableNote:
          'This environment is not subject to the edge access control: the authorized-address list applies only in production.',
        unmatchedTitle: 'Authorized addresses no application declares',
        columnIp: 'IP address',
        columnCredential: 'Credential',
        columnCalls: 'Calls',
        columnLastSeen: 'Last call',
        observedCaption: 'Addresses observed over the last %{days} days.',
        observedEmptyTitle: 'No calls observed',
        observedEmptyBody: 'No credential of this application has called in the last %{days} days.',
        dialog: {
          title: 'Edit declared egress IP addresses',
          fieldIps: 'Declared egress IP addresses',
          fieldIpsHelp:
            'Enter an IPv4 or IPv6 address and press Enter to add it. Replaces the full declared set.',
          submit: 'Save',
          cancel: 'Cancel',
          success: 'The declared egress IP addresses were updated successfully.',
        },
      },
    },
    credentials: {
      pageTitle: 'API credentials',
      pageSubtitle: 'API keys issued for the registered applications.',
      list: {
        emptyTitle: 'No API credentials found',
        emptyBody:
          'Issue an API credential for an application to authenticate its server-to-server requests.',
        errorTitle: 'The API credentials list could not be loaded',
        columnApplication: 'Application',
        columnKeyId: 'Key identifier',
        columnStatus: 'Status',
        columnScope: 'Tenant scope',
        columnRotatedAt: 'Last rotated',
        columnCreatedAt: 'Created at',
        viewDetail: 'View credential detail',
        rowsPerPage: 'Credentials per page',
        createLabel: 'Issue credential',
        neverRotated: 'Never rotated',
      },
      filters: {
        applicationCode: 'Applications',
        applicationCodeHelp: 'Enter an application code and press Enter to add it.',
      },
      status: { active: 'Active', revoked: 'Revoked' },
      scope: { allTenants: 'All tenants', specificTenants: 'Specific tenants' },
      create: {
        title: 'Issue API credential',
        fieldApplication: 'Application',
        fieldApplicationHelp: 'The calling application this credential belongs to.',
        fieldAllowAllTenants: 'Allow all tenants',
        fieldAllowedTenantCodes: 'Authorized tenants',
        fieldAllowedTenantCodesHelp: 'Enter a tenant code and press Enter to add it.',
        fieldIpAllowlist: 'IP allowlist',
        fieldIpAllowlistHelp:
          'Enter an IP or CIDR range and press Enter to add it. Leave empty to accept any source IP.',
        submit: 'Issue',
        cancel: 'Cancel',
      },
      secretPanel: {
        createdTitle: 'Credential issued',
        rotatedTitle: 'Credential rotated',
        warning:
          'Copy these values now. They are shown exactly once and cannot be retrieved again by any operation, not even by an administrator. If you lose them, issue a new credential or rotate this one.',
        fieldSecret: 'Secret',
        fieldSigningSecret: 'Signing secret',
        copy: 'Copy',
        copied: 'Copied to clipboard.',
        close: 'I have copied the values, close',
        closeConfirmTitle: 'Close without confirming the copy?',
        closeConfirmBody: 'Once you close this panel, these values will never be shown again.',
        closeConfirmConfirm: 'Close anyway',
        closeConfirmCancel: 'Go back',
      },
      detail: {
        back: 'Back to the credentials list',
        notFoundTitle: 'Credential not found',
        notFoundBody: 'Check the identifier or go back to the credentials list.',
        fieldId: 'Identifier',
        fieldApplication: 'Application',
        fieldKeyId: 'Key identifier',
        fieldStatus: 'Status',
        fieldScope: 'Tenant scope',
        fieldIpAllowlist: 'IP allowlist',
        ipAllowlistEmpty: 'No restriction: any source IP is accepted.',
        fieldRotatedAt: 'Last rotated',
        fieldCreatedAt: 'Created at',
        neverRotated: 'Never rotated',
        rotateAction: 'Rotate',
        revokeAction: 'Revoke',
        editScopeAction: 'Edit tenant scope',
        editIpAllowlistAction: 'Edit IP allowlist',
        revokedNotice: 'This credential has been revoked. It can no longer authenticate requests.',
      },
      rotateDialog: {
        title: 'Rotate this credential',
        body: 'A new secret and signing secret will be generated. The current material will stop working immediately and cannot be recovered afterwards.',
        submit: 'Rotate',
        cancel: 'Cancel',
      },
      revokeDialog: {
        title: 'Revoke this credential',
        body: 'A revoked credential can no longer authenticate requests. This action cannot be undone.',
        fieldReason: 'Reason (optional)',
        submit: 'Revoke',
        cancel: 'Cancel',
        success: 'The credential was revoked successfully.',
      },
      scopeDialog: {
        title: 'Edit tenant scope',
        fieldAllowAllTenants: 'Allow all tenants',
        fieldAllowedTenantCodes: 'Authorized tenants',
        fieldAllowedTenantCodesHelp:
          'Enter a tenant code and press Enter to add it. Replaces the entire set.',
        submit: 'Save',
        cancel: 'Cancel',
        success: 'The tenant scope was updated successfully.',
      },
      ipAllowlistDialog: {
        title: 'Edit IP allowlist',
        fieldIpAllowlist: 'IP allowlist',
        fieldIpAllowlistHelp:
          'Enter an IP or CIDR range and press Enter to add it. Replaces the entire set; leave empty to accept any source IP.',
        submit: 'Save',
        cancel: 'Cancel',
        success: 'The IP allowlist was updated successfully.',
      },
    },
    gatewayAccounts: {
      pageTitle: 'Gateway accounts',
      pageSubtitle: 'Payment gateway accounts configured for tenants.',
      list: {
        emptyTitle: 'No gateway accounts found',
        emptyBody: 'Create a gateway account for a tenant to start accepting payments through it.',
        errorTitle: 'The gateway accounts list could not be loaded',
        columnTenant: 'Tenant',
        columnGateway: 'Gateway',
        columnAlias: 'Alias',
        columnMode: 'Mode',
        columnStatus: 'Status',
        columnCreatedAt: 'Created at',
        viewDetail: 'View gateway account detail',
        rowsPerPage: 'Accounts per page',
        createLabel: 'Create gateway account',
      },
      filters: {
        tenantCode: 'Tenants',
        tenantCodeHelp: 'Enter a tenant code and press Enter to add it.',
        gateway: 'Gateway',
      },
      mode: { test: 'Test', live: 'Live' },
      status: { active: 'Active', suspended: 'Suspended' },
      gateway: {
        redsys: 'Redsys',
        stripe: 'Stripe',
        adyen: 'Adyen',
        payten: 'Payten',
        plexo: 'Plexo',
      },
      create: {
        title: 'Create gateway account',
        sourceLabel: 'Credentials source',
        sourceReal: 'Real credentials',
        sourcePreset: 'Sandbox preset',
        fieldTenant: 'Tenant',
        fieldTenantHelp: 'The tenant this account belongs to.',
        fieldGateway: 'Gateway',
        fieldMode: 'Mode',
        fieldAlias: 'Alias',
        fieldCapabilities: 'Capabilities',
        fieldCapabilitiesHelp: 'Enter a capability and press Enter to add it.',
        fieldExternalAccountCode: 'External account code',
        fieldExternalTerminalId: 'External terminal identifier',
        fieldExternalEndpointPrefix: 'Merchant endpoint prefix',
        fieldExternalEndpointPrefixHelp: 'Only for gateways that give each merchant its own live endpoint address. Leave empty when the gateway did not provide one.',
        optionalSuffix: '(optional)',
        submit: 'Create',
        cancel: 'Cancel',
        success: 'The gateway account was created successfully.',
      },
      detail: {
        back: 'Back to the gateway accounts list',
        notFoundTitle: 'Gateway account not found',
        notFoundBody: 'Check the identifier or go back to the gateway accounts list.',
        tabsGeneral: 'General',
        tabsCredentials: 'Credentials',
        fieldId: 'Identifier',
        fieldTenant: 'Tenant',
        fieldGateway: 'Gateway',
        fieldAlias: 'Alias',
        fieldMode: 'Mode',
        fieldStatus: 'Status',
        fieldCapabilities: 'Capabilities',
        capabilitiesEmpty: 'No capabilities configured.',
        fieldExternalAccountCode: 'External account code',
        fieldExternalTerminalId: 'External terminal identifier',
        fieldExternalEndpointPrefix: 'Merchant endpoint prefix',
        notSet: 'Not set',
        fieldCreatedAt: 'Created at',
        fieldPresetCode: 'Test preset',
        editAction: 'Edit account',
        deleteAction: 'Delete account',
        deleteTitle: 'Delete this account?',
        deleteBody:
          'The account “%{alias}”, its credentials and its key will be deleted. This is only possible while the account has taken no payment; the operation is recorded in the audit trail and cannot be undone.',
        deleteCancel: 'Cancel',
        deleteConfirm: 'Delete',
        deleteSuccess: 'The account was deleted successfully.',
        deleteInUse: 'This account has taken payments and cannot be deleted. Suspend it to stop using it.',
      },
      edit: {
        title: 'Edit gateway account',
        fieldAlias: 'Alias',
        fieldCapabilities: 'Capabilities',
        fieldCapabilitiesHelp: 'Enter a capability and press Enter to add it.',
        fieldStatus: 'Status',
        fieldExternalAccountCode: 'External account code',
        fieldExternalTerminalId: 'External terminal identifier',
        fieldExternalEndpointPrefix: 'Merchant endpoint prefix',
        fieldExternalEndpointPrefixHelp: 'Only for gateways that give each merchant its own live endpoint address. Leave empty when the gateway did not provide one.',
        submit: 'Save',
        cancel: 'Cancel',
        success: 'The gateway account was updated successfully.',
      },
      secrets: {
        emptyTitle: 'No credential fields have been written yet',
        emptyBody: 'Write the credential fields this gateway needs to start processing payments.',
        errorTitle: 'The credential fields could not be loaded',
        columnField: 'Field',
        columnIsSet: 'Configured',
        columnLast4: 'Last 4 characters',
        columnFingerprint: 'Fingerprint',
        columnRotatedAt: 'Last rotated',
        isSetYes: 'Yes',
        isSetNo: 'No',
        notAvailable: 'Not available',
        writeAction: 'Write credentials',
        columnActions: 'Actions',
        unusedChip: 'Unused',
        unusedTooltip: 'This gateway does not read this field. It was written by mistake and can be removed.',
        removeAction: 'Remove field',
        removeTitle: 'Remove this field?',
        removeBody:
          'Every stored version of "%{fieldName}" will be destroyed. This gateway does not read this field, so payments are unaffected. The operation is recorded in the audit trail and cannot be undone.',
        removeConfirm: 'Remove',
        removeCancel: 'Cancel',
        removeSuccess: 'The field was removed successfully.',
      },
      credentialFields: {
        redsys: {
          signing_key: {
            label: 'Merchant signing key',
            help: 'Enter the key exactly as your bank issues it. It is stored decoded.',
          },
        },
        stripe: {
          secret_key: { label: 'API secret key' },
          webhook_signing_secret: { label: 'Notification signing secret' },
        },
        adyen: {
          api_key: { label: 'API key' },
          hmac_key: {
            label: 'Notification HMAC key',
            help: 'Hexadecimal string, exactly as the provider dashboard publishes it.',
          },
        },
        payten: {
          merchant_user: { label: 'Merchant user' },
          merchant_password: { label: 'Merchant password' },
          signing_key: {
            label: 'Notification integrity secret',
            help: 'A different value from the merchant password.',
          },
        },
        plexo: {
          api_key: { label: 'API key' },
          webhook_secret: { label: 'Notification secret' },
        },
      },
      accountFields: {
        redsys: {
          externalAccountCode: 'Merchant code',
          externalTerminalId: 'Terminal',
        },
        adyen: {
          externalAccountCode: 'Merchant account',
          externalEndpointPrefix: 'Live endpoint prefix',
        },
        payten: {
          externalAccountCode: 'Merchant identifier',
        },
      },
      secretsWriteDialog: {
        title: 'Write gateway credentials',
        body: 'Values are encrypted immediately and are never shown again, not even to an administrator. Only fill in the fields you want to set or rotate.',
        manualBody:
          'The platform did not state which credentials this gateway needs. Enter the field name and its format manually.',
        fieldName: 'Field name',
        fieldNameHelp: 'The exact field name this gateway expects (e.g. merchant_code).',
        fieldValue: 'Value',
        valueEncoding: 'Format',
        valueEncodingHelp: 'Base64 only if the gateway publishes this key that way.',
        valueEncodingText: 'Text',
        valueEncodingBase64: 'Base64',
        add: 'Add field',
        remove: 'Remove',
        submit: 'Write',
        cancel: 'Cancel',
        success: 'The credential fields were written successfully.',
      },
    },
    audit: {
      pageTitle: 'Audit',
      pageSubtitle: 'Audit trail of platform activity.',
      list: {
        emptyTitle: 'No audit records found',
        emptyBody: 'Adjust the applied filters or widen the date range and try again.',
        errorTitle: 'The audit trail could not be loaded',
        columnAt: 'Date',
        columnAction: 'Action',
        columnActor: 'Actor',
        columnObject: 'Object',
        columnIp: 'IP address',
        columnMetadata: 'Metadata',
        columnTenant: 'Tenant',
        tenantPlatformWide: 'Platform-wide action',
        rowsPerPage: 'Records per page',
        viewDetail: 'View record detail',
        exportStarted: 'The CSV file download has started.',
        exportError: 'The export file could not be generated.',
      },
      filters: {
        dateFrom: 'Date from',
        dateTo: 'Date to',
        actor: 'Actor',
        actorHelp: 'Exact actor identifier.',
        actorType: 'Actor type',
        action: 'Action',
        objectType: 'Object type',
        objectTypeHelp: 'Name of the affected object type, e.g. tenant.',
        objectId: 'Object identifier',
        objectIdHelp: 'Exact identifier of the affected object.',
        objectIdNeedsObjectType: 'Select an object type first.',
        tenantCode: 'Tenants',
        tenantCodeHelp: 'Enter a tenant code and press Enter to add it.',
      },
      actorType: {
        platform_admin: 'Platform administrator',
        platform_auditor: 'Platform auditor',
        platform_staff: 'Platform staff',
        application: 'Application',
        system_process: 'System process',
        gateway: 'Gateway',
        guest: 'Guest',
      },
      objectType: {
        api_credential: 'API credential',
        gateway_account: 'Gateway account',
        gateway_secret: 'Gateway credential',
        tenant: 'Tenant',
        tenant_policy: 'Tenant policy',
        payment_intent: 'Payment intent',
        payment_operation: 'Payment operation',
        checkout_session: 'Checkout session',
        gateway_event: 'Gateway notification',
        outbound_notification: 'Outbound notification',
        report_query: 'Report query',
        audit_log: 'Audit log',
        identity: 'Identity',
      },
      metadataKey: {
        actor_role: 'Actor role',
        added: 'Added',
        allowed_tenants: 'Authorized tenants',
        alias: 'Alias',
        amount: 'Amount',
        application_id: 'Application identifier',
        attempt: 'Attempt',
        attempted_object_id: 'Attempted object identifier',
        attempted_object_type: 'Attempted object type',
        attempted_status: 'Attempted status',
        attempted_user: 'Attempted user',
        changed_fields: 'Changed fields',
        component: 'Component',
        consumed_ip: 'Consuming IP',
        current_status: 'Current status',
        currency: 'Currency',
        external_event_id: 'External event identifier',
        field_name: 'Field name',
        filters_hash: 'Filters hash',
        fingerprint: 'Fingerprint',
        format: 'Format',
        gateway: 'Gateway',
        gateway_account_id: 'Gateway account identifier',
        intent_id: 'Payment intent identifier',
        ip: 'IP',
        ip_allowlist_set: 'IP allowlist configured',
        key: 'Key',
        key_id: 'Key identifier',
        key_id_prefix: 'Key identifier prefix',
        key_management_ref: 'Key management reference',
        kind: 'Kind',
        mfa_used: 'Second factor used',
        mode: 'Mode',
        name: 'Name',
        new_status: 'New status',
        new_value: 'New value',
        notification_id: 'Notification identifier',
        operation_id: 'Operation identifier',
        order_reference: 'Order reference',
        payment_intent_id: 'Payment intent identifier',
        previous_key_id: 'Previous key identifier',
        previous_status: 'Previous status',
        previous_value: 'Previous value',
        previous_version: 'Previous version',
        new_version: 'New version',
        preset_code: 'Test bench preset code',
        process_id: 'Process identifier',
        reason: 'Reason',
        removed: 'Removed',
        required_role_or_scope: 'Required role or scope',
        result_count: 'Results returned',
        role: 'Role',
        row_count: 'Rows exported',
        signature_ok: 'Valid signature',
        size_bytes: 'Size in bytes',
        status: 'Status',
        status_code: 'Status code',
        tenant_code: 'Tenant code',
        tenant_codes_requested: 'Tenants requested',
        tenant_id: 'Tenant identifier',
        token_fingerprint: 'Token fingerprint',
        trigger: 'Change trigger',
        type: 'Type',
        unauthorized_tenant_codes: 'Unauthorized tenants',
        url_host: 'Notification host',
        user_id: 'User identifier',
        version: 'Version',
      },
      action: {
        credential: {
          created: 'API credential created',
          rotated: 'API credential rotated',
          revoked: 'API credential revoked',
          allowed_tenants_changed: 'Credential tenant scope changed',
          ip_allowlist_changed: 'Credential IP allowlist changed',
        },
        gateway_account: {
          created: 'Gateway account created',
          updated: 'Gateway account updated',
          status_changed: 'Gateway account status changed',
        },
        gateway_secret: {
          written: 'Gateway credential field written',
          rotated: 'Gateway credential field rotated',
          retired: 'Gateway credential field retired',
        },
        vault: {
          dek_unwrapped: 'Encryption key unwrapped',
          credential_decrypted: 'Gateway credential decrypted',
        },
        tenant: {
          created: 'Tenant created',
          status_changed: 'Tenant status changed',
          tag_changed: 'Tenant tag changed',
        },
        tenant_policy: {
          changed: 'Tenant policy changed',
        },
        payment_intent: {
          created: 'Payment intent created',
          status_changed: 'Payment intent status changed',
          transition_rejected: 'Payment intent status transition rejected',
        },
        payment_operation: {
          recorded: 'Payment operation recorded',
        },
        checkout_session: {
          consumed: 'Checkout session consumed',
          access_denied: 'Checkout session access denied',
        },
        gateway_event: {
          received: 'Gateway notification received',
          signature_invalid: 'Gateway notification signature invalid',
          duplicate_discarded: 'Duplicate gateway notification discarded',
        },
        notification: {
          enqueued: 'Outbound notification enqueued',
          attempted: 'Outbound notification attempted',
          exhausted: 'Outbound notification exhausted',
          resent_manually: 'Outbound notification resent manually',
        },
        report: {
          queried: 'Report queried',
          exported: 'Report exported',
          query_rejected: 'Report query rejected',
        },
        auth: {
          login_succeeded: 'Login succeeded',
          login_failed: 'Login failed',
          access_denied: 'Access denied',
          credential_rejected: 'API credential rejected',
        },
        audit_log: {
          queried: 'Audit log queried',
          exported: 'Audit log exported',
        },
        audit: {
          recording_paused: 'Audit recording paused',
          recording_resumed: 'Audit recording resumed',
        },
        test_bench: {
          preset_linked: 'Test provider linked',
          payment_launched: 'Test payment launched',
        },
      },
      detail: {
        title: 'Audit record detail',
        close: 'Close',
        fieldId: 'Identifier',
        fieldAt: 'Date',
        fieldActor: 'Actor',
        fieldActorType: 'Actor type',
        fieldAction: 'Action',
        fieldObjectType: 'Object type',
        fieldObjectId: 'Object identifier',
        fieldTenant: 'Tenant',
        tenantPlatformWide: 'Platform-wide action, no associated tenant',
        fieldIp: 'Origin IP',
        ipNotAvailable: 'Not available',
        metadataTitle: 'Action data',
        metadataEmpty: 'This action has no additional data.',
        booleanYes: 'Yes',
        booleanNo: 'No',
      },
    },
    testBench: {
      pageTitle: 'Test payment',
      pageSubtitle: 'Launch a signed test payment and follow its timeline live.',
      unavailable: {
        title: 'This feature is not available right now',
        body: 'Contact support if you need to run a test payment.',
      },
      launch: {
        title: 'Launch a test payment',
        readOnlyNotice: 'Your role only allows viewing the test payments launched.',
        fieldTenant: 'Tenant',
        fieldGatewayAccount: 'Gateway account',
        fieldGatewayAccountHelp: 'Select a tenant to see its available gateway accounts.',
        noAccountsForTenant: 'This tenant has no gateway accounts configured.',
        fieldAmount: 'Amount (minor units)',
        fieldAmountHelp: 'Equivalent to',
        fieldCurrency: 'Currency',
        submit: 'Launch test payment',
      },
      result: {
        title: 'Test payment launched successfully',
        fieldOrderReference: 'Reference',
        openSessionAction: 'Open payment page',
      },
      recent: {
        title: 'Recent test payments',
        emptyTitle: 'No test payment has been launched yet',
        emptyBody: 'Launch a test payment to start following its full lifecycle.',
        errorTitle: 'The test payment list could not be loaded',
        columnOrderReference: 'Reference',
        columnTenant: 'Tenant',
        columnAmount: 'Amount',
        columnStatus: 'Status',
        columnCreatedAt: 'Created at',
        viewTimelineAction: "View this payment's timeline",
      },
      timeline: {
        title: 'Live timeline',
        emptyBody: 'No event has been recorded for this payment yet.',
        errorTitle: 'The payment timeline could not be loaded',
        refreshAction: 'Refresh',
        pollingNotice: 'Refreshing automatically every few seconds…',
      },
      timelineEvent: {
        created: 'Payment created',
        session_consumed: 'Payment session consumed',
        redirected: 'Customer redirected to the gateway',
        webhook_received: 'Gateway notification received',
        status_changed: 'Payment status updated',
        notification_sent: 'Notification sent to the application',
        notification_delivered: 'Notification delivered successfully',
        notification_failed: 'The notification could not be delivered',
        sink_verified: 'Notification verified by the test sink',
      },
      preset: {
        linkAction: 'Link test provider',
        dialogTitle: 'Link test provider',
        fieldTenant: 'Tenant',
        fieldPreset: 'Test provider',
        noPresets: 'No test providers are available right now.',
        fieldAlias: 'Alias',
        fieldAliasHelp: 'Leave blank to use the alias proposed by the test provider.',
        submit: 'Link',
        cancel: 'Cancel',
        success: 'The test account has been linked successfully.',
      },
      testCard: {
        title: 'Test card',
        fieldPan: 'Card number',
        fieldExpiry: 'Expiry',
        fieldCvv: 'Security code',
        fieldChallengeCode: 'Challenge code',
        copy: 'Copy',
        copied: 'Value copied to clipboard.',
      },
    },
  },
};

export default en;
